// api/core/auth/authService.ts
import { apiClient, baseApiClient } from '../apiClient';
import { getChameleonHeaders } from '../chameleonState';
// Direct path, not the '@/utils' barrel — keeps the auth chunk out of the
// heavyweight export utilities the barrel drags in.
import { clearViewSelection } from '@/utils/viewSelection';
import type { PermissionLevel } from '@/types';
import { type EntityType, entityTypeFromKey } from '@/types/common';
import type { LoginApiPayload, LoginResponse, LoginSessionUser, ViewerIdentityResponse } from '@/types/auth';

interface ParsedRole {
    entity_type: EntityType;
    permission_level: PermissionLevel;
    isAdmin: boolean;
    isSuperAdmin: boolean;
}

/**
 * Parse role string into structured permission data
 * e.g., "ROLE_BROADCASTER_ADMIN", "ROLE_AGENCY", "ROLE_ADMIN", "ROLE_ADTECH_ADMIN"
 */
const parseRole = (roleString: string): ParsedRole => {
    // Handle the superuser case
    if (roleString === 'ROLE_ADMIN') {
        return {
            entity_type: 'SYSTEM',
            permission_level: 'SUPER_ADMIN',
            isAdmin: true,
            isSuperAdmin: true,
        };
    }

    // Parse standard roles: ROLE_ENTITY_TYPE_LEVEL
    const parts = roleString.split('_');

    if (parts.length < 2) {
        console.warn(`Unexpected role format: ${roleString}`);
        return {
            entity_type: 'SYSTEM',
            permission_level: 'USER',
            isAdmin: false,
            isSuperAdmin: false,
        };
    }

    const rawType = parts[1];
    const entity_type = entityTypeFromKey(rawType) ?? (rawType as EntityType);
    const permission_level: PermissionLevel = parts[2] === 'ADMIN' ? 'ADMIN' : 'USER';

    if (!entityTypeFromKey(rawType)) {
        console.warn(`Unknown entity type: ${rawType} in role ${roleString}`);
    }

    return {
        entity_type,
        permission_level,
        isAdmin: permission_level === 'ADMIN',
        isSuperAdmin: false,
    };
};

export const authService = {
    getViewerIdentity: async (): Promise<ViewerIdentityResponse> => {
        const response = await apiClient.get<ViewerIdentityResponse>('/me', { headers: getChameleonHeaders() });
        return response.data;
    },

    refreshToken: async (): Promise<boolean> => {
        // Cookie-based refresh — tokens are managed server-side via HttpOnly cookies.
        // Simply rethrow on failure; the interceptor decides what happens next.
        await baseApiClient.post('/refresh-token');
        return true;
    },

    login: async (email: string, password: string): Promise<LoginResponse> => {
        try {
            const response = await baseApiClient.post<LoginApiPayload>('/login', { email, password });
            // Tokens are set in HttpOnly cookies.
            // NOTE: user_id and channels from the payload are intentionally not captured here.
            const {
                role,
                role_id,
                entity_id,
                country_code,
                currency_code,
                name,
                country_list,
                entity_type,
                partition_id,
                help_email,
            } = response.data;

            const parsedRole = parseRole(role);

            const user: LoginSessionUser = {
                ...parsedRole,
                email,
                name,
                role,
                role_id,
                entity_id,
                entity_type: entityTypeFromKey(entity_type) ?? (entity_type.toUpperCase() as EntityType),
                country_code,
                currency_code,
                country_list,
                partition_id: partition_id ?? null,
                help_email: help_email ?? undefined,
            };

            return { status: 'success', user };
        } catch (error) {
            return {
                status: 'failure',
                error: error instanceof Error ? error.message : String(error),
            };
        }
    },

    logout: async (): Promise<void> => {
        // Clear local session indicator immediately to prevent race conditions.
        // HttpOnly cookies are cleared server-side by the /logout endpoint.
        localStorage.removeItem('user');

        // Clear the previous session's customer/date selection. Left in place, a
        // stale selectedCustomerId belongs to the prior user and triggers 403s when
        // the next user (who can't see that customer) logs in on the same browser.
        clearViewSelection();

        try {
            await baseApiClient.post('/logout');
        } catch (e) {
            console.warn('Logout API call failed', e);
        }
    },

    isAuthenticated: (): boolean => {
        // Auth uses HttpOnly cookies — the token is invisible to JS.
        // The "user" key in localStorage is purely a client-side session indicator
        // (set on login, cleared on logout/401). It does NOT prove the cookie is valid;
        // the server's 401 response on any API call is the authoritative check.
        return !!localStorage.getItem('user');
    },

    isAdTechUser: (): boolean => {
        const user = JSON.parse(localStorage.getItem('user') || '{}') as Partial<LoginSessionUser>;
        return user.entity_type === 'ADTECH';
    },

    isMultiCountry: (): boolean => {
        const user = JSON.parse(localStorage.getItem('user') || '{}') as Partial<LoginSessionUser>;
        return Array.isArray(user.country_list) && user.country_list.length > 1;
    },
};
